import { getSettingChatWorkingHours } from "@faire/help-center-utils/settings/getSettingChatWorkingHours";
import { getSettingPhoneWorkingHours } from "@faire/help-center-utils/settings/getSettingPhoneWorkingHours";
import addDays from "date-fns/addDays";
import formatDuration from "date-fns/formatDuration";
import intervalToDuration from "date-fns/intervalToDuration";
import isWeekend from "date-fns/isWeekend";
import set from "date-fns/set";

type AvailableTime = {
  time: string | undefined;
  unit: "hours" | "minutes" | "days" | undefined;
};

export type SupportType = "phone" | "chat" | "email";

export const convertDateToTargetTimeZoneDate = (
  date: Date,
  targetTimeZone: string
): string => {
  const convertedDate = date.toLocaleString("en-US", {
    timeZone: targetTimeZone,
    hour12: false,
  });
  return convertedDate;
};

export const convertDateHoursInMilliseconds = (date: string): number => {
  const hoursInMilliseconds = new Date(date).getHours() * 60 * 60 * 1000;
  return hoursInMilliseconds;
};

export const isWeekendFromDate = (date: string): boolean => {
  return isWeekend(new Date(date));
};

export const availableInHoursMinutesOrDays = (
  supportType: SupportType
): AvailableTime => {
  let workingHoursSetting;
  if (supportType === "chat") {
    workingHoursSetting = getSettingChatWorkingHours();
  } else {
    workingHoursSetting = getSettingPhoneWorkingHours();
  }
  const { start_at, end_at, timezone } = workingHoursSetting;
  if (!start_at || !end_at) {
    return {
      time: "",
      unit: undefined,
    };
  }
  const currentDate = new Date();
  let tomorrowDate;

  const targetDate = convertDateToTargetTimeZoneDate(
    currentDate,
    timezone ?? "America/New_York"
  );
  const hours = targetDate.split(":")[0]?.split(",")[1]?.trim();
  if (Number(hours) >= end_at / (60 * 60 * 1000)) {
    tomorrowDate = addDays(new Date(targetDate), 1);
  } else {
    tomorrowDate = new Date(targetDate);
  }
  const tomorrowAvailableDate = set(tomorrowDate, {
    hours: start_at / (60 * 60 * 1000),
    minutes: 0,
    seconds: 0,
    milliseconds: 0,
  });

  // Check if today is a weekend
  const dayOfWeek = new Date(targetDate).getDay();
  if (dayOfWeek === 0 || dayOfWeek === 6) {
    // 0 = Sunday, 6 = Saturday
    const daysUntilMonday = dayOfWeek === 0 ? 1 : 2;
    return {
      time: String(daysUntilMonday),
      unit: "days",
    };
  }
  let duration = intervalToDuration({
    start: new Date(targetDate),
    end: tomorrowAvailableDate,
  });
  // The difference hours between current time and tomorrow's start time needs to minus 1
  if (duration.hours && duration.hours > 1) {
    if (duration.minutes === 0) {
      duration = {
        ...duration,
        hours: duration.hours,
      };
    } else {
      duration = { ...duration, hours: duration.hours + 1 };
    }
  }

  const isLessThanOneHour = !!(
    duration.hours === 0 &&
    duration.minutes &&
    duration.minutes < 60
  );

  const formattedDuration = formatDuration(duration, {
    format: isLessThanOneHour ? ["minutes"] : ["hours"],
  });

  const [time, unit] = formattedDuration.split(" ");
  if (isLessThanOneHour) {
    if (duration.seconds && duration.seconds !== 0) {
      return {
        time: String(Number(time) + 1),
        unit: "minutes",
      };
    }
  }
  return {
    time,
    unit: unit === "hours" || unit === "hour" ? "hours" : "minutes",
  };
};
